import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ListGroup, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import {
  FaTachometerAlt,
  FaStore,
  FaUsers,
  FaBox,
  FaFileInvoiceDollar,
  FaTools,
  FaShippingFast,
  FaClipboardList,
  FaBuilding,
  FaBoxes,
  FaWarehouse,
  FaAddressBook,
  FaLayerGroup,
  FaTruckLoading,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaListAlt,
  FaQuestionCircle,
  FaKey,
  FaDatabase,
} from 'react-icons/fa';
import { selectViewMode, STAFF_ROLES } from '../../redux/selectors';
import { setViewMode, startImpersonation } from '../../redux/actions';
import { getAdminASNs, getAdminOutboundSubmissions, impersonateClient } from '../../utils/api';
import { getStoresDataCached, getAdminClientsCached } from '../../utils/metadataCache';
import './index.scss';

const VIEW_MODES = [
  { key: 'super_admin', label: 'Super Admin' },
  { key: 'owner', label: '3PL Owner' },
  { key: 'client', label: '3PL Client' },
];

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapsed }) => {
  const user = useSelector((state) => state.user);
  const viewMode = useSelector(selectViewMode);
  const selectedStore = useSelector((state) => state.stores?.selectedStore ?? null);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [pendingAsnCount, setPendingAsnCount] = useState(0);
  const [pendingOutboundCount, setPendingOutboundCount] = useState(0);
  const [impersonating, setImpersonating] = useState(false);
  const [clientSwitchError, setClientSwitchError] = useState(null);

  const [showClientPicker, setShowClientPicker] = useState(false);
  const [clientPickerList, setClientPickerList] = useState([]);
  const [clientPickerLoading, setClientPickerLoading] = useState(false);
  const pickerRef = useRef(null);

  const isSuperAdmin = user.role === 'Super Admin';
  const hideAdminItems = isSuperAdmin && viewMode !== 'super_admin';
  const isOwnerView = user.role === 'Admin' || (isSuperAdmin && viewMode === 'owner');
  const isClientView = user.role === 'client' || viewMode === 'client';

  useEffect(() => {
    let active = true;

    if (!['Super Admin', 'Admin'].includes(user.role) || isClientView) {
      setPendingAsnCount(0);
      setPendingOutboundCount(0);
      return () => {
        active = false;
      };
    }

    getAdminASNs({ status: 'pending_approval', limit: 1, offset: 0 }).then((res) => {
      if (active) setPendingAsnCount(res?.total ?? res?.count ?? 0);
    });
    getAdminOutboundSubmissions({ status: 'pending_approval', limit: 1, offset: 0 }).then((res) => {
      if (active) setPendingOutboundCount(res?.count ?? 0);
    });

    return () => {
      active = false;
    };
  }, [user.role, isClientView]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowClientPicker(false);
      }
    };
    if (showClientPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showClientPicker]);

  const isStaff = STAFF_ROLES.includes(user.role);
  const isBillingStaff = ['Fee Entry', 'Billing Viewer'].includes(user.role);
  const isReceivingClerk = user.role === 'Receiving Clerk';
  const isFulfillmentStaff = user.role === 'Fulfillment Staff';
  const isWarehouseStaff = user.role === 'Warehouse Staff';

  const MAIN_MENU = [
    {
      name: 'Dashboard',
      icon: <FaTachometerAlt />,
      to: '/dashboard',
      isActive: location.pathname === '/dashboard',
      access: !isStaff,
    },
    {
      name: 'Stores',
      icon: <FaStore />,
      to: '/stores',
      isActive: location.pathname.startsWith('/stores'),
      access: user.role === 'Super Admin' && !hideAdminItems,
    },
    {
      name: 'Products',
      icon: <FaLayerGroup />,
      to: '/admin/products',
      isActive: location.pathname.startsWith('/admin/products'),
      access: user.role === 'Super Admin' && !hideAdminItems,
    },
    {
      name: 'My 3PL',
      icon: <FaBuilding />,
      to: '/my-store',
      isActive:
        location.pathname.startsWith('/my-store') ||
        (isOwnerView && location.pathname.startsWith('/stores/')),
      access: isOwnerView,
    },
    {
      name: 'Products',
      icon: <FaBoxes />,
      to: '/owner/products',
      isActive: location.pathname.startsWith('/owner/products'),
      access: isOwnerView || isClientView,
    },
    {
      name: 'Inventory',
      icon: <FaWarehouse />,
      to: '/owner/inventory',
      isActive: location.pathname.startsWith('/owner/inventory'),
      access: isOwnerView || isClientView || isWarehouseStaff,
    },
    {
      name: 'Purchases',
      icon: <FaTruckLoading />,
      to: '/purchases',
      isActive: location.pathname.startsWith('/purchases'),
      access:
        isOwnerView ||
        (user.role === 'Super Admin' && !hideAdminItems) ||
        isReceivingClerk ||
        isWarehouseStaff,
    },
    {
      name: 'Clients',
      icon: <FaAddressBook />,
      to: '/my-clients',
      isActive: location.pathname.startsWith('/my-clients'),
      access: isOwnerView,
    },
    {
      name: 'Clients',
      icon: <FaBox />,
      to: '/clients',
      isActive:
        location.pathname.startsWith('/clients') &&
        !location.pathname.startsWith('/my-clients') &&
        !location.pathname.endsWith('/ledger'),
      access: (user.role === 'Super Admin' && !hideAdminItems) || isStaff,
    },
    {
      name: 'Orders',
      icon: <FaClipboardList />,
      to: '/orders',
      isActive: location.pathname.startsWith('/orders'),
      access: !isStaff || isFulfillmentStaff || isWarehouseStaff,
    },
    {
      name: 'Transfers',
      icon: <FaShippingFast />,
      to: '/transfers',
      isActive: location.pathname.startsWith('/transfers'),
      access: ['Super Admin', 'Admin'].includes(user.role) && !isClientView,
    },
    {
      name: 'Invoices',
      icon: <FaFileInvoiceDollar />,
      to: '/invoices',
      isActive: location.pathname.includes('/invoices'),
      access: !isStaff || isBillingStaff,
    },
    {
      name: 'Ledger',
      icon: <FaListAlt />,
      to: '/ledger',
      isActive: location.pathname === '/ledger' || location.pathname.endsWith('/ledger'),
      access: (['Super Admin', 'Admin'].includes(user.role) && !isClientView) || isBillingStaff,
    },
    {
      name: 'Users',
      icon: <FaUsers />,
      to: '/users',
      isActive: location.pathname === '/users',
      access: user.role === 'Super Admin' && !hideAdminItems,
    },
    {
      name: 'Users',
      icon: <FaUsers />,
      to: '/owner/users',
      isActive: location.pathname.startsWith('/owner/users'),
      access: isOwnerView,
    },
    {
      name: 'Tools',
      icon: <FaTools />,
      to: '/tools',
      isActive: location.pathname.startsWith('/tools'),
      access: ['Super Admin', 'Admin'].includes(user.role) && !hideAdminItems && !isClientView,
    },
    {
      name: 'Occupancy Preview',
      icon: <FaDatabase />,
      to: '/admin/billing/occupancy-preview',
      isActive: location.pathname === '/admin/billing/occupancy-preview',
      access: user.role === 'Super Admin' && !hideAdminItems,
    },
    {
      name: 'Missing Carrier Costs',
      icon: <FaDatabase />,
      to: '/admin/billing/missing-shipment-costs',
      isActive: location.pathname === '/admin/billing/missing-shipment-costs',
      access:
        user.role === 'Admin' || (user.role === 'Super Admin' && !hideAdminItems && !isClientView),
    },
    {
      name: 'Shipments',
      icon: <FaShippingFast />,
      to: '/shipments',
      isActive:
        location.pathname.startsWith('/shipments') ||
        location.pathname.startsWith('/asn') ||
        location.pathname.startsWith('/outbound'),
      badge:
        pendingAsnCount + pendingOutboundCount > 0 ? pendingAsnCount + pendingOutboundCount : null,
      access:
        (['Super Admin', 'Admin'].includes(user.role) && !isClientView) ||
        isReceivingClerk ||
        isWarehouseStaff,
    },
    {
      name: 'Shipments',
      icon: <FaShippingFast />,
      to: '/client/shipments',
      isActive:
        location.pathname.startsWith('/client/shipments') ||
        location.pathname.startsWith('/client/asn') ||
        location.pathname.startsWith('/client/outbound'),
      badge: null,
      access: isClientView,
    },
    {
      name: 'API Keys',
      icon: <FaKey />,
      to: '/api-keys',
      isActive: location.pathname.startsWith('/api-keys'),
      access: ['Super Admin', 'Admin'].includes(user.role) && !isClientView,
    },
    {
      name: 'Help',
      icon: <FaQuestionCircle />,
      to: '/help',
      isActive: location.pathname.startsWith('/help'),
      access: !isClientView,
    },
  ];

  const doImpersonate = async (client) => {
    setImpersonating(true);
    setClientSwitchError(null);
    setShowClientPicker(false);
    const res = await impersonateClient(client.id);
    if (res?.success === false) {
      setImpersonating(false);
      setClientSwitchError(res.message || 'Impersonation failed. Please try again.');
      return;
    }
    const token = res?.token || res?.accessToken || res?.jwt || res?.data?.token;
    if (token) {
      try {
        sessionStorage.setItem('skusavvy_client_token', token);
      } catch (e) {}
      const maxAge = res.expiresIn ?? 3600;
      document.cookie = `SKUSAVVY=${token}; path=/; max-age=${maxAge}; SameSite=Lax`;
    } else {
      console.warn(
        '[SKUSavvy] No token found in impersonation response — client portal API calls may fail.',
      );
    }
    dispatch(setViewMode('client'));
    dispatch(
      startImpersonation({
        clientName: client.name,
        clientId: client.id,
        previousViewMode: viewMode,
        ownerLogoUrl: selectedStore?.logoUrl ?? null,
      }),
    );
    setImpersonating(false);
    navigate('/client/dashboard');
  };

  const openClientPicker = async () => {
    setShowClientPicker(true);
    setClientPickerLoading(true);
    setClientSwitchError(null);
    setClientPickerList([]);

    const storesRes = await getStoresDataCached();
    const stores = storesRes?.stores ?? [];

    if (stores.length === 0) {
      setClientPickerLoading(false);
      setClientSwitchError('No stores found.');
      setShowClientPicker(false);
      return;
    }

    const clientArrays = await Promise.all(
      stores.map((s) => getAdminClientsCached(s.storeId).then((r) => r?.clients ?? [])),
    );
    const seen = new Set();
    const merged = clientArrays.flat().filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });

    setClientPickerLoading(false);
    if (merged.length === 0) {
      setClientSwitchError('No clients found. Add a client first.');
      setShowClientPicker(false);
      return;
    }
    setClientPickerList(merged);
  };

  const handleViewModeSwitch = (mode) => {
    if (mode === viewMode) return;
    setClientSwitchError(null);

    if (mode === 'client') {
      openClientPicker();
      return;
    }

    dispatch(setViewMode(mode));
    navigate('/dashboard');
  };

  const visibleItems = MAIN_MENU.filter((m) => m.access);

  return (
    <div className={`main-sidebar${isOpen ? ' is-open' : ''}${isCollapsed ? ' is-collapsed' : ''}`}>
      <ListGroup variant="flush" className="list">
        {visibleItems.map((menu) => {
          const item = (
            <ListGroup.Item
              action
              as={Link}
              to={menu.to}
              key={menu.to}
              onClick={onClose}
              className={`${menu.isActive ? 'active-item' : ''}${isCollapsed ? ' collapsed-item' : ''}`}>
              <span className="sidebar-icon">{menu.icon}</span>
              {!isCollapsed && <span className="sidebar-label">{menu.name}</span>}
              {menu.badge != null && (
                <span
                  className={`sidebar-badge${isCollapsed ? ' sidebar-badge--dot' : ' ms-auto'}`}>
                  {isCollapsed ? (menu.badge > 9 ? '9+' : menu.badge) : menu.badge}
                </span>
              )}
            </ListGroup.Item>
          );

          if (isCollapsed) {
            return (
              <OverlayTrigger
                key={menu.to}
                placement="right"
                overlay={<Tooltip id={`sidebar-tip-${menu.to}`}>{menu.name}</Tooltip>}>
                {item}
              </OverlayTrigger>
            );
          }

          return item;
        })}
      </ListGroup>

      <button
        className="sidebar-collapse-btn"
        onClick={onToggleCollapsed}
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
        {isCollapsed ? <FaChevronRight size={12} /> : <FaChevronLeft size={12} />}
      </button>

      {isSuperAdmin && !isCollapsed && (
        <div className="role-switcher">
          <div className="role-switcher__label">View as</div>
          <div className="role-switcher__buttons" ref={pickerRef}>
            {VIEW_MODES.map((m) => (
              <button
                key={m.key}
                disabled={impersonating}
                className={`role-switcher__btn${viewMode === m.key ? ' role-switcher__btn--active' : ''}`}
                onClick={() => handleViewModeSwitch(m.key)}>
                {m.key === 'client' && impersonating ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-1"
                      style={{ width: '0.65rem', height: '0.65rem' }}
                    />
                    Loading…
                  </>
                ) : (
                  <>
                    {m.label}
                    {m.key === 'client' && viewMode !== 'client' && (
                      <FaChevronDown size={9} className="ms-auto" style={{ opacity: 0.5 }} />
                    )}
                  </>
                )}
              </button>
            ))}

            {showClientPicker && (
              <div className="role-switcher__client-picker">
                {clientPickerLoading ? (
                  <div className="role-switcher__client-picker-loading">
                    <Spinner animation="border" size="sm" />
                    <span>Loading clients…</span>
                  </div>
                ) : (
                  <ul className="role-switcher__client-list">
                    {clientPickerList.map((c) => (
                      <li key={c.id}>
                        <button
                          className="role-switcher__client-item"
                          onClick={() => doImpersonate(c)}>
                          {c.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          {clientSwitchError && <div className="role-switcher__error">{clientSwitchError}</div>}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
